import { DATABASE_PERMISSION_GROUPS, type Database, type DatabaseAction } from './types';

/** What the viewer may do with a database (the server enforces every action anyway). */
export function dbCan(
  database: Pick<Database, 'my_permissions'> | undefined,
  action: DatabaseAction,
) {
  return database?.my_permissions.includes(action) ?? false;
}

/** Full control of the database: the workspace owner or the database owner. */
export const isDatabaseOwner = (database: Pick<Database, 'my_role'> | undefined) =>
  database?.my_role === 'owner';

/** What the viewer may do, one flag per permission (the server enforces each one again). */
export function databaseAbilities(database: Pick<Database, 'my_permissions'> | undefined) {
  const has = (action: DatabaseAction) => dbCan(database, action);
  return {
    createTables: has('database.create_tables'),
    editTables: has('database.edit_tables'),
    deleteTables: has('database.delete_tables'),
    createColumns: has('database.create_columns'),
    editColumns: has('database.edit_columns'),
    deleteColumns: has('database.delete_columns'),
    viewRecords: has('database.view_records'),
    createRecords: has('database.create_records'),
    editRecords: has('database.edit_records'),
    deleteRecords: has('database.delete_records'),
    exportRecords: has('database.export_records'),
  };
}

/** The server's catalog arranged in the groups of the access screens (unknown ids go last). */
export function groupDatabaseCatalog(catalog: readonly string[]): Record<string, string[]> {
  const known = new Set<string>();
  const groups: Record<string, string[]> = {};
  for (const [group, members] of Object.entries(DATABASE_PERMISSION_GROUPS)) {
    const present = members.filter((permission) => catalog.includes(permission));
    present.forEach((permission) => known.add(permission));
    if (present.length > 0) groups[group] = present;
  }
  const rest = catalog.filter((permission) => !known.has(permission));
  if (rest.length > 0) groups['database'] = rest;
  return groups;
}
